import { client } from "./claims";
import {
  AffectedPattern,
  ApiProblem,
  AuthoringCatalogue,
  BindingBatch,
  BindingResult,
  BusinessUpgradePreview,
  CanonicalApplicationResult,
  CanonicalApplicationSummary,
  CanonicalApplicationView,
  CanonicalPreviewResult,
  CanonicalSelection,
  CanonicalTakeoverRequest,
  CanonicalTakeoverResult,
  EligibilityReview,
  GuidanceReferenceView,
  HistoryEvent,
  Page,
  PatternDefinition,
  PatternExample,
  PatternPreviewResult,
  PatternRecord,
  ReferenceMutation,
  SelectionV1,
  TemplateSource,
  TemplateSummary,
  WorkflowDefinition,
  WorkflowPin,
  WorkflowPreviewResult,
  WorkflowRecord,
  WorkflowUsePage,
  WorkflowVersion,
} from "../components/caf-workflows/workflowTypes";
const base = "/api/admin/caf-workflows";
const workflow = (id: string) => `${base}/${encodeURIComponent(id)}`;
const pattern = (id: string) =>
  `/api/admin/caf-corpus/entries/${encodeURIComponent(id)}/pattern`;
export const listWorkflows = (cursor?: string) =>
  client
    .get<Page<WorkflowRecord>>(base, { params: { cursor } })
    .then((r) => r.data);
export const getWorkflow = (id: string) =>
  client.get<WorkflowRecord>(workflow(id)).then((r) => r.data);
export const getWorkflowVersion = (id: string, version: number) =>
  client
    .get<WorkflowVersion>(`${workflow(id)}/versions/${version}`)
    .then((r) => r.data);
export const getWorkflowHistory = (id: string, cursor?: string) =>
  client
    .get<Page<HistoryEvent>>(`${workflow(id)}/history`, { params: { cursor } })
    .then((r) => r.data);
export const getAuthoringCatalogue = (page?: { cursor?: string; limit?: number }) =>
  client
    .get<AuthoringCatalogue>(`${base}/authoring-catalogue`, ...(page ? [{ params: page }] : []))
    .then((r) => r.data);
export const saveWorkflow = (
  id: string,
  definition: WorkflowDefinition,
  expected_revision: number,
) =>
  client
    .put<WorkflowRecord>(`${workflow(id)}/draft`, {
      definition,
      expected_revision,
    })
    .then((r) => r.data);
export const previewWorkflow = (definition: WorkflowDefinition) =>
  client
    .post<WorkflowPreviewResult>(`${base}/preview`, { definition })
    .then((r) => r.data);
/**
 * No-write canonical preparation preview for 1..128 unique reviewed entry IDs.
 * It never saves, publishes, activates or binds; opening a proposed definition
 * as a draft is a separate, ordinary saveWorkflow CAS.
 */
export const previewCanonicalDefinitions = (reviewed_entry_ids: string[]) =>
  client
    .post<CanonicalPreviewResult>(`${base}/canonical-preview`, {
      reviewed_entry_ids,
    })
    .then((r) => r.data);
/** Pure before/after preview; acceptance is an ordinary saveWorkflow CAS. */
export const previewBusinessUpgrade = (definition: WorkflowDefinition) =>
  client
    .post<BusinessUpgradePreview>(`${base}/business-settings/upgrade-preview`, {
      definition,
    })
    .then((r) => r.data);
export const publishWorkflow = (
  id: string,
  expected_revision: number,
  reason: string,
  reviewed_templates: Record<string, string>,
  reviewed_catalogue?: import('./emailCatalogue').CatalogueReviews,
) =>
  client
    .post<WorkflowVersion>(`${workflow(id)}/publish`, {
      expected_revision,
      reason,
      validation_evidence: { reviewed_templates, ...(reviewed_catalogue ? { reviewed_catalogue } : {}) },
    })
    .then((r) => r.data);
export const controlWorkflow = (
  id: string,
  operation: "activate" | "pause",
  expected_revision: number,
  reason: string,
) =>
  client
    .post<WorkflowRecord>(`${workflow(id)}/${operation}`, {
      expected_revision,
      reason,
    })
    .then((r) => r.data);
export const listTemplates = () =>
  client
    .get<{ items: TemplateSummary[] }>(`${base}/email-templates`)
    .then((r) => r.data.items);
export const inspectTemplate = (id: string, version?: number) =>
  client
    .get<TemplateSource>(`${base}/email-templates/${encodeURIComponent(id)}`, {
      params: { version },
    })
    .then((r) => r.data);
export const importTemplate = (
  id: string,
  html: string,
  text: string | null,
  expected_version: number,
  reason: string,
) =>
  client
    .post<TemplateSource>(
      `${base}/email-templates/${encodeURIComponent(id)}/import`,
      { html, text, expected_version, reason },
    )
    .then((r) => r.data);
export const getPattern = (id: string) =>
  client.get<PatternRecord>(pattern(id)).then((r) => r.data);
export const savePattern = (
  id: string,
  definition: PatternDefinition,
  expected_version: number,
  reason: string,
) =>
  client
    .put<PatternRecord>(`${pattern(id)}/draft`, {
      definition,
      expected_version,
      reason,
    })
    .then((r) => r.data);
export const approvePattern = (
  id: string,
  expected_version: number,
  reason: string,
  eligibility_review?: {
    review_hash: string;
    evidence_selections: Array<{
      node_id: string;
      path: string;
      evidence_refs: string[];
    }>;
    approved_review_items: Array<{ node_id: string; path: string; item_hash: string }>;
  },
) =>
  client
    .post<PatternRecord>(`${pattern(id)}/approve`, {
      expected_version,
      reason,
      ...(eligibility_review ? { eligibility_review } : {}),
    })
    .then((r) => r.data);
export const pausePattern = (
  id: string,
  expected_version: number,
  reason: string,
) =>
  client
    .post<PatternRecord>(`${pattern(id)}/pause`, {
      paused: true,
      expected_version,
      reason,
    })
    .then((r) => r.data);
export const previewPattern = (
  definition: PatternDefinition,
  examples: PatternExample[],
  eligibility_review?: {
    review_hash: string;
    evidence_selections: Array<{
      node_id: string;
      path: string;
      evidence_refs: string[];
    }>;
    approved_review_items: Array<{ node_id: string; path: string; item_hash: string }>;
  },
) =>
  client
    .post<PatternPreviewResult>(`${base}/pattern-preview`, {
      definition,
      examples,
      ...(eligibility_review ? { eligibility_review } : {}),
    })
    .then((r) => r.data);
export const listAffectedPatterns = (id: string, cursor?: string) =>
  client
    .get<
      Page<AffectedPattern> & {
        authority: string;
        current_source_authority_verified: false;
      }
    >(`${workflow(id)}/patterns`, { params: { cursor } })
    .then((r) => r.data);
/** Current precedent entries using this workflow (navigation only, <=100/page). */
export const listWorkflowUses = (id: string, cursor?: string, limit = 50) =>
  client
    .get<WorkflowUsePage>(`${workflow(id)}/uses`, { params: { cursor, limit } })
    .then((r) => r.data);
export function apiProblem(error: unknown): ApiProblem {
  const e = error as {
    message?: string;
    response?: {
      status?: number;
      data?: { error?: string; path?: string; code?: string };
    };
  };
  return {
    message:
      e.response?.data?.error ||
      e.message ||
      "The request could not be completed.",
    path: e.response?.data?.path,
    code: e.response?.data?.code,
    conflict: e.response?.status === 409,
  };
}

/* Guidance references: one exact source, audited, never recognition. */
const reference = (entryId: string) =>
  `/api/admin/caf-corpus/entries/${encodeURIComponent(entryId)}/workflow-reference`;
export const getWorkflowReference = (entryId: string, sourceSender: string) =>
  client
    .get<GuidanceReferenceView>(reference(entryId), { params: { source_sender: sourceSender } })
    .then((r) => r.data);
/** 202 pending / 409 conflict or held / 422 matcher-review held still resolve with the mutation envelope. */
const envelope = (error: unknown): ReferenceMutation => {
  const data = (error as { response?: { data?: Partial<ReferenceMutation> } })?.response?.data;
  if (data && typeof data.status === "string") return data as ReferenceMutation;
  throw error;
};
export const setWorkflowReference = (entry: SelectionV1, target: WorkflowPin, reason: string) =>
  client
    .put<ReferenceMutation>(reference(entry.entry_id), {
      source_sender: entry.source_sender,
      workflow_id: target.workflow_id,
      workflow_version: target.workflow_version,
      expected_version: entry.expected_version,
      ...(entry.reviewed_token ? { reviewed_token: entry.reviewed_token } : {}),
      reason,
    })
    .then((r) => r.data, envelope);
export const removeWorkflowReference = (entry: SelectionV1, reason: string) =>
  client
    .request<unknown>({
      url: reference(entry.entry_id),
      method: "delete",
      data: {
        source_sender: entry.source_sender,
        expected_version: entry.expected_version,
        ...(entry.reviewed_token ? { reviewed_token: entry.reviewed_token } : {}),
        reason,
      },
    })
    .then((r) => r.data as ReferenceMutation, envelope);
/** Audited repair of a held link (malformed reference/origin or a foreign proof). */
export const repairWorkflowReference = (entry: SelectionV1, reason: string) =>
  client
    .post<ReferenceMutation>(`${reference(entry.entry_id)}/repair`, {
      source_sender: entry.source_sender,
      expected_version: entry.expected_version,
      ...(entry.reviewed_token ? { reviewed_token: entry.reviewed_token } : {}),
      reason,
    })
    .then((r) => r.data, envelope);
export const reconcileReferenceOperation = (path: string, reason: string) =>
  client
    .post<{ operation_id: string; status: string; reason_code: string | null }>(path, { reason })
    .then((r) => r.data);

/* Selected binding updates: one immutable target, 1..50 explicit sources. */
const updates = `${base}/binding-updates`;
export const previewBindingUpdates = (
  preparation_id: string,
  target: WorkflowPin,
  selections: SelectionV1[],
  reason: string,
) =>
  client
    .post<BindingBatch>(`${updates}/preview`, {
      preparation_id,
      target_workflow_id: target.workflow_id,
      target_workflow_version: target.workflow_version,
      selections,
      reason,
    })
    .then((r) => r.data);
export const listBindingUpdates = (
  params: { preparation_id?: string; cursor?: string; limit?: number } = {},
) =>
  client
    .get<{ items: Array<Record<string, unknown>>; next_cursor: string | null }>(updates, { params })
    .then((r) => r.data);
export const getBindingUpdate = (batchId: string) =>
  client.get<BindingBatch>(`${updates}/${encodeURIComponent(batchId)}`).then((r) => r.data);
export const applyBindingUpdates = (
  batchId: string,
  preview_hash: string,
  reason: string,
  eligibility_reviews: EligibilityReview[],
) =>
  client
    .post<BindingResult>(`${updates}/${encodeURIComponent(batchId)}/apply`, {
      preview_hash,
      reason,
      eligibility_reviews,
    })
    .then((r) => r.data);
export const resumeBindingUpdates = (batchId: string, reason: string) =>
  client
    .post<BindingResult>(`${updates}/${encodeURIComponent(batchId)}/resume`, { reason })
    .then((r) => r.data);

/* Canonical applications: explicit selection -> frozen manifest -> audited apply/recover/verify. */
const canonical = `${base}/canonical-applications`;
const canonicalBatch = (batchId: string) => `${canonical}/${encodeURIComponent(batchId)}`;
/**
 * 202 pending resolves normally. A 409 that carries the stored result
 * (a competing authorizer, or a refused/held takeover) resolves with it so the
 * outcome and its recovery link stay visible; anything else rejects.
 */
const withResult = <T,>(error: unknown): T => {
  const data = (error as { response?: { status?: number; data?: { result?: T } } })?.response;
  if (data?.status === 409 && data.data?.result) return data.data.result;
  throw error;
};
export const prepareCanonicalApplication = (
  preparation_id: string,
  selections: CanonicalSelection[],
  reason: string,
) =>
  client
    .post<CanonicalApplicationView>(`${canonical}/prepare`, { preparation_id, selections, reason })
    .then((r) => r.data);
export const listCanonicalApplications = (
  params: { cursor?: string; limit?: number; workflow_id?: string } = {},
) =>
  client
    .get<{ items: CanonicalApplicationSummary[]; next_cursor: string | null }>(canonical, { params })
    .then((r) => r.data);
export const getCanonicalApplication = (batchId: string) =>
  client.get<CanonicalApplicationView>(canonicalBatch(batchId)).then((r) => r.data);
export const applyCanonicalApplication = (batchId: string, manifest_hash: string, reason: string) =>
  client
    .post<CanonicalApplicationResult>(`${canonicalBatch(batchId)}/apply`, { manifest_hash, reason })
    .then((r) => r.data, withResult<CanonicalApplicationResult>);
export const verifyCanonicalApplication = (batchId: string) =>
  client.get<CanonicalApplicationResult>(`${canonicalBatch(batchId)}/verify`).then((r) => r.data);
export const recoverCanonicalApplication = (batchId: string, reason: string) =>
  client
    .post<CanonicalApplicationResult>(`${canonicalBatch(batchId)}/recover`, { reason })
    .then((r) => r.data, withResult<CanonicalApplicationResult>);
export const takeOverCanonicalAllocation = (request: CanonicalTakeoverRequest) =>
  client
    .post<CanonicalTakeoverResult>(`${canonical}/allocation-takeover`, request)
    .then((r) => r.data, withResult<CanonicalTakeoverResult>);
